import { useMemo } from "react";
import { useReveal } from "../hooks/useReveal";

/* Lightweight stylized world map: ellipses approximate continents,
   pulsing dots highlight export regions, animated arcs originate from India.
   Equirectangular-ish projection on a 1000x500 viewBox. */

const HUB = { x: 695, y: 240, label: "Guntur, India" }; // origin

const REGIONS = [
    { x: 235, y: 225, label: "USA" },
    { x: 295, y: 320, label: "Brazil" },
    { x: 510, y: 175, label: "UK / EU" },
    { x: 545, y: 220, label: "Spain / Italy" },
    { x: 615, y: 230, label: "UAE / Saudi" },
    { x: 660, y: 215, label: "Iran" },
    { x: 760, y: 220, label: "China" },
    { x: 825, y: 235, label: "Japan" },
    { x: 770, y: 295, label: "SE Asia" },
    { x: 870, y: 380, label: "Australia" },
    { x: 575, y: 320, label: "Africa" },
];

// Simplified continent shapes (stylized)
const CONTINENTS = [
    // North America
    "M150,150 C200,120 280,130 320,170 C340,200 320,250 280,270 C240,280 190,260 170,220 Z",
    // South America
    "M270,310 C310,300 340,330 330,400 C320,450 290,470 270,440 C250,400 250,340 270,310 Z",
    // Europe
    "M495,160 C540,150 590,160 600,190 C600,210 570,220 540,215 C510,210 490,190 495,160 Z",
    // Africa
    "M540,230 C590,220 620,260 620,320 C620,370 590,410 560,400 C530,380 525,310 540,230 Z",
    // Middle East / India
    "M620,210 C670,200 720,220 730,250 C730,290 700,310 670,300 C640,290 620,260 620,210 Z",
    // Asia
    "M720,150 C800,140 870,170 880,220 C880,260 830,280 780,270 C740,260 720,210 720,150 Z",
    // SE Asia
    "M770,280 C810,275 830,300 815,325 C795,340 770,330 760,310 C758,295 765,285 770,280 Z",
    // Australia
    "M840,360 C890,355 920,385 905,420 C880,440 845,430 830,400 C825,385 830,365 840,360 Z",
];

function Arc({ from, to, delay = 0 }) {
    const mx = (from.x + to.x) / 2;
    const my = (from.y + to.y) / 2 - 60 - Math.abs(from.x - to.x) * 0.08;
    const d = `M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`;
    return (
        <g>
            <path
                d={d}
                fill="none"
                stroke="url(#arcGrad)"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeDasharray="280"
                strokeDashoffset="280"
                style={{
                    animation: `dash 4s ${delay}s ease-out infinite`,
                }}
            />
        </g>
    );
}

export default function GlobalMap() {
    const ref = useReveal();
    const arcs = useMemo(() => REGIONS.map((r, i) => ({ r, delay: i * 0.25 })), []);
    return (
        <section
            id="global"
            data-testid="global-section"
            className="bg-white py-24 sm:py-32"
        >
            <style>{`
                @keyframes dash {
                    0% { stroke-dashoffset: 280; opacity: 0; }
                    20% { opacity: 1; }
                    70% { stroke-dashoffset: 0; opacity: 1; }
                    100% { stroke-dashoffset: -280; opacity: 0; }
                }
            `}</style>
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                <div ref={ref} className="reveal mb-12 grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.22em] text-brand-red">
                            Global Presence
                        </span>
                        <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.025em] sm:text-6xl">
                            Shipping to 30+ countries.
                        </h2>
                    </div>
                    <p className="text-ink-muted lg:col-span-5 lg:pt-3">
                        From the Middle East to North America, our spices reach
                        retailers, blenders and food processors across four
                        continents — handled by trusted freight forwarders.
                    </p>
                </div>

                <div className="rounded-3xl border border-hairline bg-neutral-bg p-4 sm:p-8">
                    <svg
                        viewBox="0 0 1000 500"
                        className="block h-auto w-full"
                        role="img"
                        aria-label="World map of RP Traders export regions"
                        data-testid="global-map-svg"
                    >
                        <defs>
                            <linearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="oklch(0.42 0.18 27)" />
                                <stop offset="100%" stopColor="oklch(0.78 0.13 75)" />
                            </linearGradient>
                            <radialGradient id="dotGrad">
                                <stop offset="0%" stopColor="oklch(0.55 0.2 27)" />
                                <stop offset="100%" stopColor="oklch(0.42 0.18 27)" />
                            </radialGradient>
                        </defs>

                        {CONTINENTS.map((d, i) => (
                            <path
                                key={i}
                                d={d}
                                fill="oklch(0.92 0 0)"
                                stroke="oklch(0.85 0 0)"
                                strokeWidth="0.8"
                            />
                        ))}

                        {arcs.map(({ r, delay }, i) => (
                            <Arc key={i} from={HUB} to={r} delay={delay} />
                        ))}

                        {REGIONS.map((r) => (
                            <g key={r.label}>
                                <circle
                                    cx={r.x}
                                    cy={r.y}
                                    r="6"
                                    fill="oklch(0.42 0.18 27 / 0.25)"
                                    className="pulse-ring"
                                    style={{ transformOrigin: `${r.x}px ${r.y}px` }}
                                />
                                <circle cx={r.x} cy={r.y} r="3.5" fill="url(#dotGrad)" />
                                <text
                                    x={r.x + 8}
                                    y={r.y - 8}
                                    fontSize="11"
                                    fill="oklch(0.3 0 0)"
                                    fontFamily="Inter, sans-serif"
                                    fontWeight="500"
                                >
                                    {r.label}
                                </text>
                            </g>
                        ))}

                        {/* Origin hub */}
                        <g>
                            <circle
                                cx={HUB.x}
                                cy={HUB.y}
                                r="9"
                                fill="oklch(0.78 0.13 75 / 0.3)"
                                className="pulse-ring"
                                style={{ transformOrigin: `${HUB.x}px ${HUB.y}px` }}
                            />
                            <circle cx={HUB.x} cy={HUB.y} r="5" fill="oklch(0.78 0.13 75)" stroke="white" strokeWidth="2" />
                            <text
                                x={HUB.x + 10}
                                y={HUB.y + 18}
                                fontSize="12"
                                fill="oklch(0.18 0 0)"
                                fontFamily="Inter, sans-serif"
                                fontWeight="700"
                            >
                                {HUB.label}
                            </text>
                        </g>
                    </svg>
                </div>

                <div className="mt-8 flex flex-wrap gap-2 text-xs">
                    {["UAE", "Saudi Arabia", "USA", "UK", "Germany", "Spain", "Italy", "Brazil", "China", "Japan", "Malaysia", "Australia", "South Africa", "Vietnam"].map(
                        (c) => (
                            <span
                                key={c}
                                className="rounded-full border border-hairline bg-white px-3 py-1 text-ink-muted"
                            >
                                {c}
                            </span>
                        )
                    )}
                </div>
            </div>
        </section>
    );
}
